import { formatMoney } from '../../utils/money';
import { useState } from "react";
import {
	Box,
	Container,
	Grid,
	Paper,
	Typography,
	Card,
	CardContent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	Tab,
	IconButton,
	Tooltip,
	Button,
	TextField,
	MenuItem,
	Stack,
	Divider
} from "@mui/material";
import { useShopDetails } from "../../hooks/use-shop";
import { useQuery } from "@apollo/client";
import { GET_COMMISSION_REPORT } from "../../gql/queries";
import { ReportStats } from "../../components/report/stats";
import { useSearch } from "../../hooks/use-search";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Loader from "../../components/generic/Loader";
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import { ReportDonut } from "../../components/report/donut";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`stylist-tabpanel-${index}`}
			aria-labelledby={`stylist-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ py: 3 }}>
					{children}
				</Box>
			)}
		</div>
	);
}

const CommissionReportPage = () => {
	const { shopId, locationId, profile } = useShopDetails();
	const { SearchComponent, searchQuery } = useSearch();
	const [currentTab, setCurrentTab] = useState(0);
	const [selectedStylist, setSelectedStylist] = useState<string | null>(null);
	const [view, setView] = useState<'table' | 'chart'>('table');

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	// Fetch commission report data
	const { data, loading } = useQuery(GET_COMMISSION_REPORT, {
		variables: {
			query: {
				shopId,
				locationId,
				startDate: searchQuery.from,
				endDate: searchQuery.to,
				stylistUserId: searchQuery.userId,
				timezone: searchQuery.timezone,
			},
		},
		fetchPolicy: 'network-only'
	});

	const {
		totalStylists = 0,
		totalCommissionAmount = 0,
		totalSalesAmount = 0,
		stylistReports = [],
	} = data?.commissionReport || {};

	// Handle stylist selection for detail view
	const handleSelectStylist = (stylistId: string) => {
		setSelectedStylist(stylistId);
		setCurrentTab(1); // Switch to detail tab
	};

	// Find the selected stylist data
	const selectedStylistData = stylistReports.find(
		(stylist: any) => stylist.stylistUserId === selectedStylist
	);

	return (
		<>
			<Loader isLoading={loading} />
			<Box component="main" sx={{ flexGrow: 1, py: 8 }}>
				<Container maxWidth="xl">
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
						<Typography variant="h4">{profile.staffLabel} Commission Report</Typography>
						<Stack direction="row" spacing={2}>
							<Tooltip title={view === 'table' ? "Switch to Chart View" : "Switch to Table View"}>
								<IconButton
									onClick={() => setView(view === 'table' ? 'chart' : 'table')}
								>
									{view === 'table' ? <PieChartIcon /> : <BarChartIcon />}
								</IconButton>
							</Tooltip>
							<Button
								startIcon={<FileDownloadIcon />}
								variant="outlined"
							>
								Export Report
							</Button>
						</Stack>
					</Box>

					{/* Search date range and stylist selector */}
					{SearchComponent(loading)}

					{/* Summary Stats */}
					<Box sx={{ mt: 4 }}>
						<Grid container spacing={4}>
							<Grid item xs={12} md={4}>
								<ReportStats
									sx={{ height: '100%' }}
									value={formatMoney(totalSalesAmount)}
									title="Total Sales Amount"
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<ReportStats
									sx={{ height: '100%' }}
									value={formatMoney(totalCommissionAmount)}
									title="Total Commission Amount"
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<ReportStats
									sx={{ height: '100%' }}
									value={totalStylists.toString()}
									title={`${profile.staffLabel} count`}
								/>
							</Grid>
						</Grid>
					</Box>

					{/* Tabs for summary and details */}
					<Box sx={{ mt: 4 }}>
						<Card>
							<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
								<Tabs
									value={currentTab}
									onChange={handleTabChange}
									aria-label="commission report tabs"
								>
									<Tab label="Summary" />
									<Tab
										label={selectedStylist ? `${selectedStylistData?.stylistName} Details` : "Stylist Details"}
										disabled={!selectedStylist}
									/>
								</Tabs>
							</Box>

							{/* Summary Tab */}
							<TabPanel value={currentTab} index={0}>
								{view === 'table' ? (
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>{profile.staffLabel} Name</TableCell>
												<TableCell>Total Orders</TableCell>
												<TableCell>Service Sales</TableCell>
												<TableCell>Product Sales</TableCell>
												<TableCell>Total Sales</TableCell>
												<TableCell>Service Commission</TableCell>
												<TableCell>Product Commission</TableCell>
												<TableCell>Total Commission</TableCell>
												<TableCell>Actions</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{stylistReports.map((stylist: any) => (
												<TableRow key={stylist.stylistUserId} hover>
													<TableCell>{stylist.stylistName}</TableCell>
													<TableCell>{stylist.totalOrders}</TableCell>
													<TableCell>{formatMoney(stylist.totalServiceAmount)}</TableCell>
													<TableCell>{formatMoney(stylist.totalProductAmount)}</TableCell>
													<TableCell>{formatMoney(stylist.totalAmount)}</TableCell>
													<TableCell>{formatMoney(stylist.totalServiceCommission)}</TableCell>
													<TableCell>{formatMoney(stylist.totalProductCommission)}</TableCell>
													<TableCell>{formatMoney(stylist.totalCommission)}</TableCell>
													<TableCell>
														<Button
															variant="text"
															size="small"
															onClick={() => handleSelectStylist(stylist.stylistUserId)}
														>
															View Details
														</Button>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								) : (
									<Box sx={{ mt: 3 }}>
										<Grid container spacing={3}>
											<Grid item xs={12} md={6}>
												<Paper
													elevation={3}
													sx={{
														height: '100%',
														p: 3,
														display: 'flex',
														flexDirection: 'column',
													}}
												>
													<Typography variant="h6" gutterBottom>
														{profile.staffLabel} Commission Distribution
													</Typography>
													<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
														<ReportDonut
															title="Commission Distribution"
															chartSeries={stylistReports.map((stylist: any) => stylist.totalCommission)}
															labels={stylistReports.map((stylist: any) =>
																`${stylist.stylistName}: ${formatMoney(stylist.totalCommission)}`
															)}
														/>
													</Box>
												</Paper>
											</Grid>
											<Grid item xs={12} md={6}>
												<Paper
													elevation={3}
													sx={{
														height: '100%',
														p: 3,
														display: 'flex',
														flexDirection: 'column',
													}}
												>
													<Typography variant="h6" gutterBottom>
														{profile.staffLabel} Sales Distribution
													</Typography>
													<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
														<ReportDonut
															title="Sales Distribution"
															chartSeries={stylistReports.map((stylist: any) => stylist.totalAmount)}
															labels={stylistReports.map((stylist: any) =>
																`${stylist.stylistName}: ${formatMoney(stylist.totalAmount)}`
															)}
														/>
													</Box>
												</Paper>
											</Grid>
										</Grid>
									</Box>
								)}
							</TabPanel>

							{/* Detail Tab */}
							<TabPanel value={currentTab} index={1}>
								{selectedStylistData && (
									<Box>
										<Grid container spacing={4}>
											<Grid item xs={12} md={3}>
												<ReportStats
													sx={{ height: '100%' }}
													value={formatMoney(selectedStylistData.totalAmount)}
													title="Total Sales"
												/>
											</Grid>
											<Grid item xs={12} md={3}>
												<ReportStats
													sx={{ height: '100%' }}
													value={formatMoney(selectedStylistData.totalCommission)}
													title="Total Commission"
												/>
											</Grid>
											<Grid item xs={12} md={3}>
												<ReportStats
													sx={{ height: '100%' }}
													value={`${((selectedStylistData.totalCommission / selectedStylistData.totalAmount) * 100).toFixed(2)}%`}
													title="Commission Rate"
												/>
											</Grid>
											<Grid item xs={12} md={3}>
												<ReportStats
													sx={{ height: '100%' }}
													value={selectedStylistData.totalOrders.toString()}
													title="Orders"
												/>
											</Grid>
										</Grid>

										{/* Service Commissions */}
										<Box sx={{ mt: 4 }}>
											<Typography variant="h6" gutterBottom>
												Service Commissions
											</Typography>
											<Card>
												<Table>
													<TableHead>
														<TableRow>
															<TableCell>Service</TableCell>
															<TableCell>Quantity</TableCell>
															<TableCell>Total Amount</TableCell>
															<TableCell>Commission %</TableCell>
															<TableCell>Commission Amount</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{selectedStylistData.serviceCommissions.map((service: any) => (
															<TableRow key={service.serviceId} hover>
																<TableCell>{service.serviceName}</TableCell>
																<TableCell>{service.quantity}</TableCell>
																<TableCell>{formatMoney(service.totalAmount)}</TableCell>
																<TableCell>{service.commissionPercentage}%</TableCell>
																<TableCell>{formatMoney(service.commissionAmount)}</TableCell>
															</TableRow>
														))}
														<TableRow sx={{ bgcolor: 'action.hover' }}>
															<TableCell colSpan={2}><strong>Total</strong></TableCell>
															<TableCell>{formatMoney(selectedStylistData.totalServiceAmount)}</TableCell>
															<TableCell>-</TableCell>
															<TableCell>{formatMoney(selectedStylistData.totalServiceCommission)}</TableCell>
														</TableRow>
													</TableBody>
												</Table>
											</Card>
										</Box>

										{/* Product Commissions */}
										<Box sx={{ mt: 4 }}>
											<Typography variant="h6" gutterBottom>
												Product Commissions
											</Typography>
											<Card>
												<Table>
													<TableHead>
														<TableRow>
															<TableCell>Product</TableCell>
															<TableCell>Quantity</TableCell>
															<TableCell>Total Amount</TableCell>
															<TableCell>Commission %</TableCell>
															<TableCell>Commission Amount</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{selectedStylistData.productCommissions.map((product: any) => (
															<TableRow key={product.productId} hover>
																<TableCell>{product.productName}</TableCell>
																<TableCell>{product.quantity}</TableCell>
																<TableCell>{formatMoney(product.totalAmount)}</TableCell>
																<TableCell>{product.commissionPercentage}%</TableCell>
																<TableCell>{formatMoney(product.commissionAmount)}</TableCell>
															</TableRow>
														))}
														<TableRow sx={{ bgcolor: 'action.hover' }}>
															<TableCell colSpan={2}><strong>Total</strong></TableCell>
															<TableCell>{formatMoney(selectedStylistData.totalProductAmount)}</TableCell>
															<TableCell>-</TableCell>
															<TableCell>{formatMoney(selectedStylistData.totalProductCommission)}</TableCell>
														</TableRow>
													</TableBody>
												</Table>
											</Card>
										</Box>
									</Box>
								)}
							</TabPanel>
						</Card>
					</Box>
				</Container>
			</Box>
		</>
	);
};

export default CommissionReportPage;
